// The dealer's Learning Center reader — a shell over the shared reader.
//
// The reader itself now lives in features/training-reader and is the SAME component the admin
// portal's editor mounts for its Preview pane. This file is only the dealer's side of the
// injection: its completion store, its presign endpoint, its PDF exporter, and its
// completion-aware video player. Layout, tabs, the contents rail and the markdown pipeline all
// come from the feature.
//
// What stayed here and why: completion is Apollo-shaped (useModuleCompletion owns the optimistic
// cache update and its rollback toast), the asset resolver posts to this app's endpoint, and
// ModuleVideo drives the YouTube IFrame API so playback end can complete the training. None of
// that belongs in a portable feature.

import { useCallback } from 'react';
import { toast } from 'react-toastify';
import { ModuleReader as SharedModuleReader } from '@hopdrive/feature-training-reader';
import ModuleVideo from './ModuleVideo';
import { useModuleCompletion } from '../hooks/useModuleCompletion';
import { resolveAssetUrl } from '../utils/assets';
import { downloadModulePdf } from '../utils/moduleToPdf';
import type { LearningModule } from '../types';

export default function ModuleReader({ module }: { module: LearningModule }) {
  const { isCompleted, completedAt, markComplete, markIncomplete } = useModuleCompletion();
  const completed = isCompleted(module.key);

  // Mark complete and announce it — but only once the save is confirmed (never optimistically; a
  // rejected write shows its own failure toast) and only on the transition INTO complete, so a
  // re-mark or a replayed video doesn't re-toast. Capture "was it already complete?" before the
  // write.
  const completeAndAnnounce = useCallback(() => {
    const wasCompleted = isCompleted(module.key);
    void markComplete(module.key, module.id).then(saved => {
      if (saved && !wasCompleted) toast.success('Training marked complete');
    });
  }, [isCompleted, markComplete, module.key, module.id]);

  // Every completion entry point in the reader — the end-of-document card and playing the video to
  // the end — funnels through here, so the announce rule genuinely lives in one place. (The header
  // used to carry a toggle button that called the store's `toggle` directly and was the one path
  // that completed silently; it is now a display-only "Completed" chip.)
  const onToggle = useCallback(
    (next: boolean) => {
      if (next) completeAndAnnounce();
      else markIncomplete(module.key);
    },
    [completeAndAnnounce, markIncomplete, module.key],
  );

  return (
    <SharedModuleReader
      module={module}
      resolveAssetUrl={resolveAssetUrl}
      onDownloadPdf={() => downloadModulePdf(module)}
      completion={{ completed, completedAt: completedAt(module.key), onToggle }}
      // The feature's default player is a plain embed that can't report playback end. Ours drives
      // the IFrame API, so pass the reader's onEnded straight through — the "finishing the video
      // finishes the training" rule stays in the reader.
      renderVideo={({ url, onEnded }) => <ModuleVideo url={url} onEnded={onEnded} />}
      onError={toast.error}
    />
  );
}
